export interface ImageOptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'image/jpeg' | 'image/webp' | 'image/png';
}

const DEFAULT_OPTIONS: Required<ImageOptimizationOptions> = {
  maxWidth: 500,
  maxHeight: 500,
  quality: 0.85,
  format: 'image/webp',
};

export async function optimizeImage(
  file: File,
  options: ImageOptimizationOptions = {}
): Promise<{ file: File; dimensions: { width: number; height: number } }> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > opts.maxWidth || height > opts.maxHeight) {
          if (width > height) {
            width = opts.maxWidth;
            height = width / aspectRatio;
          } else {
            height = opts.maxHeight;
            width = height * aspectRatio;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not create blob from canvas'));
              return;
            }

            const extension = opts.format === 'image/webp' ? 'webp' :
                            opts.format === 'image/png' ? 'png' : 'jpg';

            const optimizedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, `.${extension}`),
              {
                type: opts.format,
                lastModified: Date.now(),
              }
            );

            resolve({
              file: optimizedFile,
              dimensions: { width, height },
            });
          },
          opts.format,
          opts.quality
        );
      };

      img.onerror = () => {
        reject(new Error('Could not load image'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Could not read file'));
    };

    reader.readAsDataURL(file);
  });
}

export async function validateImageFile(file: File): Promise<void> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size must be less than 10MB');
  }
}
